import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { DateRangePicker, FocusedInputShape } from "react-dates";
import moment, { Moment } from "moment";
import useMobileDetect from "../../../../hooks/useMobileDetect";
import "../../../../styles/react-dates-custom-theme.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMobileDetectContext } from "../../../../context/MobileDetectContext";

interface IStayDatesProps {
  from: Date | null;
  to: Date | null;
  checkinDayChange: (date: Date) => void;
  checkoutDayChange: (date: Date) => void;
  isSearchModal?: boolean;
  isOfferModal?: boolean;
  minLOS?: string | null;
  minLeadDays?: string | null;
}

const StayDates: React.FC<IStayDatesProps> = ({
  from,
  to,
  checkinDayChange,
  checkoutDayChange,
  isSearchModal,
  isOfferModal,
  minLOS,
  minLeadDays,
}) => {
  const transitionContainerRef = useRef<HTMLDivElement | null>(null);
  const { isMobileOnly } = useMobileDetectContext();
  const [startDate, setStartDate] = useState<Moment | null>(
    from ? moment(from) : null
  );
  const [endDate, setEndDate] = useState<Moment | null>(to ? moment(to) : null);
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    null
  );

  const handleDatesChange = ({
    startDate,
    endDate,
  }: {
    startDate: Moment | null;
    endDate: Moment | null;
  }) => {
    setStartDate(startDate);
    setEndDate(endDate);
    if (startDate) checkinDayChange(startDate.toDate());
    if (endDate) checkoutDayChange(endDate.toDate());
  };

  // Function to disable past dates
  const isOutsideRange = (day: Moment) => {
    return day.isBefore(moment(), "day"); // Disable all dates before today
  };

  const onCloseCalendar = () => {
    if (startDate && !endDate) {
      const minNights = minLOS ? Number(minLOS) : 1;
      const autoEndDate = moment(startDate).add(minNights, "days");

      setEndDate(autoEndDate);
      checkoutDayChange(autoEndDate.toDate());
    }
    setFocusedInput(null); // Close the calendar
  };

  // Render custom footer with "Close" button for mobile and offers modal
  const renderCalendarInfo = () => {
    const normalizedStartDate = startDate ? startDate.startOf("day") : null;
    const normalizedEndDate = endDate ? endDate.startOf("day") : null;

    const selectedDays =
      normalizedStartDate && normalizedEndDate
        ? normalizedEndDate.diff(normalizedStartDate, "days")
        : 0;

    const minRequiredDate = moment(new Date()).add(Number(minLeadDays), "days");
    const userSelectedDate = moment(normalizedStartDate);
    const diffInDays = minRequiredDate.diff(userSelectedDate, "days");

    return isMobileOnly || isOfferModal ? (
      <div className="calendar-footer text-center calendar-info-wrapper">
        <div className="calender-minimum-nights-message">
          {selectedDays && isOfferModal ? (
            <p>{selectedDays} nights selected</p>
          ) : null}
          <div>
            {minLeadDays && diffInDays > 0 ? (
              <div style={{ marginBottom: "1rem" }}>
                <span className="message">
                  This offer requires check-in at least {minLeadDays} days in
                  advance.
                </span>
              </div>
            ) : null}
            {minLOS && selectedDays < Number(minLOS) ? (
              <div style={{ marginBottom: "1rem" }}>
                <span className="message">
                  You need to select a minimum of {minLOS} nights.
                </span>
              </div>
            ) : null}
          </div>
        </div>
        <button className="calendar-close-button" onClick={onCloseCalendar}>
          Close
        </button>
      </div>
    ) : null;
  };

  const renderDayContents = (day: Moment) => {
    return (
      <span id={`day-${day.format("YYYY-MM-DD")}`}>{day.format("D")}</span>
    );
  };

  // Highlight the <td> elements for days within the `minLOS` range
  useEffect(() => {
    // Clear any previously highlighted days first
    document.querySelectorAll(".highlighted-day").forEach((element) => {
      element.classList.remove("highlighted-day");
    });

    if (startDate && minLOS && focusedInput) {
      const daysSelected = (endDate && endDate.diff(startDate, "days")) || 0;
      // Iterate through days in the range from startDate up to minLOS days
      if (daysSelected < Number(minLOS)) {
        for (let i = daysSelected; i <= Number(minLOS); i++) {
          const dayToHighlight = moment(startDate)
            .add(i, "days")
            .format("YYYY-MM-DD");
          // Skip highlighting for the start and end dates
          if (
            dayToHighlight === startDate.format("YYYY-MM-DD") ||
            (endDate && dayToHighlight <= endDate.format("YYYY-MM-DD"))
          ) {
            continue;
          }
          const dayElement = document.getElementById(`day-${dayToHighlight}`);
          const parentTd = dayElement?.closest("td");
          if (parentTd) {
            parentTd.classList.add("highlighted-day"); // Add custom class to `<td>`
          }
        }
      }
    }
  }, [startDate, endDate, minLOS, focusedInput]);
  useLayoutEffect(() => {
    const weekHeaderElement = document.querySelector(".DayPicker_weekHeaders");
    if (weekHeaderElement) {
      const parentDiv = weekHeaderElement.parentElement;
      if (parentDiv) {
        parentDiv.classList.add("mobile-width-wrapper");
      }
    }
  }, [focusedInput]);
  // Handle blur event directly on the date input to close calendar immediately when tabbing away
  const handleBlur = (event: FocusEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (
      relatedTarget &&
      !transitionContainerRef.current?.contains(relatedTarget) &&
      !document.querySelector(".calendar-info-wrapper")?.contains(relatedTarget)
    ) {
      onCloseCalendar();
    }
  };
  // Close calendar and auto-select end date if missing
  const handleFocusChange = (input: FocusedInputShape | null) => {
    if (!input && startDate && !endDate) {
      // Auto-select end date as one day after start date
      const autoEndDate = moment(startDate).add(1, "day");
      setEndDate(autoEndDate);
      checkoutDayChange(autoEndDate.toDate());
    }
    setFocusedInput(input); // Update focused input state (open/close calendar)
  };

  useEffect(() => {
    if (focusedInput) {
      const inputs = document.querySelectorAll(
        ".DateInput_input"
      ) as NodeListOf<HTMLElement>;
      inputs.forEach((input) => input.addEventListener("blur", handleBlur));

      return () => {
        inputs.forEach((input) =>
          input.removeEventListener("blur", handleBlur)
        );
      };
    }
  }, [focusedInput]);

  useEffect(() => {
    if (from) setStartDate(moment(from));
    if (to) setEndDate(moment(to));
  }, [from, to]);

  return (
    <div
      id={isSearchModal ? "search-modal-date-wrapper" : ""}
      className={isOfferModal ? `offer-dates-modal-wrapper` : ""}
      ref={transitionContainerRef}
    >
      <DateRangePicker
        startDate={startDate} // moment object or null
        startDateId="checkin-date" // unique ID for the start date input
        endDate={endDate} // moment object or null
        endDateId="checkout-date" // unique ID for the end date input
        onDatesChange={handleDatesChange} // handler function for date changes
        focusedInput={focusedInput} // currently focused input
        onFocusChange={handleFocusChange} // handler function for input focus changes
        numberOfMonths={isMobileOnly ? 1 : 2} // Number of months to show
        isOutsideRange={isOutsideRange} // Disable dates outside of a range (customize as needed)
        displayFormat="MMM DD" // Date display format
        hideKeyboardShortcutsPanel={true}
        readOnly={true}
        customArrowIcon={<FontAwesomeIcon icon="long-arrow-alt-right" />}
        renderCalendarInfo={renderCalendarInfo} // Custom footer with close button
        keepOpenOnDateSelect={true}
        minimumNights={1}
        startDatePlaceholderText="From"
        endDatePlaceholderText="To"
        renderDayContents={renderDayContents} // Add custom day rendering
      />
    </div>
  );
};

export default StayDates;
